import * as Yup from 'yup';
import { initialFilters } from '../../../utils/helper';
import moment from 'moment';
import DefaultImage from '../../../../src/assets/Images/ProductDefaultImage/default.jpg';

export const productDefaultImage = DefaultImage;

export const productSchema = (variationKey) =>
  Yup.object().shape({
    itemName: Yup.string()
      .required('Product title is required')
      .matches(
        /^(?!.*\/).*$/,
        'Product title should not contain the slash character /',
      ),
    description: Yup.string().required('Description is required'),
    // notes: Yup.string(),
    inventoryTagDTOS: Yup.array()
      .nullable()
      .min(1, 'At least one tag is required')
      .of(
        Yup.object().shape({
          id: Yup.string().required('ID is required'),
        }),
      ),
    availableFromDate: Yup.date().when('availableDateStatus', {
      is: true, // Only apply validation when availableDateStatus is true
      then: Yup.date().required('Available From Date is required'),
      otherwise: Yup.date().nullable(),
    }),
    availableToDate: Yup.date().when('availableDateStatus', {
      is: true, // Only apply validation when availableDateStatus is true
      then: Yup.date()
        .required('Available To Date is required')
        .when('availableFromDate', (availableFromDate, schema) => {
          return availableFromDate
            ? schema.min(
                availableFromDate,
                'To Date cannot be before From Date',
              )
            : schema;
        }),
      otherwise: Yup.date().nullable(),
    }),
    price: Yup.number()
      .test({
        name: 'price',
        exclusive: false,
        params: {},
        message: 'Price cannot be empty',
        test: function (value) {
          return this.parent.floristVariationDTOS?.length ||
            this?.parent?.variationDTOS?.length > 0
            ? true
            : value || value === 0;
        },
      })
      .nullable(),
    oldPrice: Yup.number()
      .test({
        name: 'oldPrice',
        exclusive: false,
        params: {},
        message: 'Your discount is greater than original value',
        test: function (value) {
          return this.parent.floristVariationDTOS?.length ||
            this?.parent?.variationDTOS?.length > 0
            ? true
            : !value || value <= this.parent.price;
        },
      })
      .nullable(),
    categoryDTO: Yup.array()
      .nullable()
      .min(1, 'At least one category is required')
      .of(Yup.string()),
    [variationKey]: Yup.array()
      .nullable()
      .of(
        Yup.object().shape({
          title: Yup.string().required('Title is required'),
          price: Yup.number()
            .required('Price is required')
            .moreThan(0, 'price must be greater than 0'),
          discountedPrice: Yup.number()
            .test({
              name: 'discountedPrice',
              exclusive: false,
              params: {},
              message: 'Your discount is greater than original value',
              test: function (value) {
                return !value || value <= this.parent.price;
              },
            })
            .nullable(),
          holidayPrice: Yup.number()
            .moreThan(0, 'Holiday price must be greater than zero')
            .nullable(),
        }),
      ),
  });

export const productDefaultValues = {
  activated: true,
  hidden: false,
  localDeliveryOnly: true,
  itemName: '',
  description: '',
  notes: '',
  price: undefined,
  rCompany: undefined,
  rFirst: undefined,
  rLast: undefined,
  rAddress: undefined,
  rPhone: undefined,
  rCity: undefined,
  rState: undefined,
  rZip: undefined,
  currency: 'USD',
  floristVariationDTOS: [],
  inventoryTagDTOSList: [],
  categoryDTO: [],
  addonsDTOS: [],
  weightType: 'oz',
  availableDateStatus: false,
  availableFromDate: moment(new Date()).format('YYYY-MM-DD'),
  availableToDate: moment(new Date()).format('YYYY-MM-DD'),
};

export const productCategories = [
  {
    id: 1,
    name: 'All',
  },
  {
    id: 2,
    name: 'Birthday',
  },
  {
    id: 3,
    name: 'Anniversary',
  },
  {
    id: 4,
    name: 'Winter Bouquets',
  },
  {
    id: 5,
    name: 'Modern',
  },
  {
    id: 6,
    name: 'Celebration',
  },
  {
    id: 7,
    name: 'Ceremony',
  },
];

export const stockStatus = [
  {
    id: 0,
    name: 'Not Available',
  },
  {
    id: 1,
    name: 'Available',
  },
];

export const stockStatusList = [
  {
    id: 1,
    name: 'All',
  },
  {
    id: 2,
    name: 'Available',
  },
  {
    id: 3,
    name: 'Limited',
  },
  {
    id: 4,
    name: 'Inactive',
  },
];

export const getImageUrl = (
  product,
  isCatalogue = false,
  currentTime = moment(),
) => {
  const VariationDTOKey = isCatalogue
    ? 'variationDTOS'
    : 'floristVariationDTOS';

  let image;

  if (product?.inventoryImages) {
    const imageDTO =
      product?.inventoryImages?.find((item) => item?.thumbnail) ??
      product?.inventoryImages?.[0];

    const uploadedTime = imageDTO?.uploadedOn
      ? moment.utc(imageDTO?.uploadedOn)
      : moment();
    const timeDifference = currentTime.diff(uploadedTime, 'seconds');

    image =
      timeDifference < 10
        ? imageDTO?.url
        : imageDTO?.thumbnailUrl || imageDTO?.url || image;
  }

  if (product?.floristInventoryImages) {
    const imageDTO =
      product?.floristInventoryImages?.find((item) => item?.thumbnail) ??
      product?.floristInventoryImages?.[0];

    const uploadedTime = imageDTO?.uploadedOn
      ? moment.utc(imageDTO?.uploadedOn)
      : moment();
    const timeDifference = currentTime.diff(uploadedTime, 'seconds');
    image =
      timeDifference < 10
        ? imageDTO?.url
        : imageDTO?.thumbnailUrl || imageDTO?.url || image;
  }

  //assign default Variation image if default variation exist
  if (
    product?.[VariationDTOKey]?.find((p) => p?.defaultVariation)?.imageDTOList
      ?.length
  ) {
    const ImageList = product?.[VariationDTOKey]?.find(
      (p) => p?.defaultVariation,
    )?.imageDTOList;
    const imageDTO =
      ImageList?.find((item) => item?.thumbnail) ?? ImageList?.[0];

    const uploadedTime = imageDTO?.uploadedOn
      ? moment.utc(imageDTO?.uploadedOn)
      : moment();
    const timeDifference = currentTime.diff(uploadedTime, 'seconds');

    image =
      timeDifference < 10
        ? imageDTO?.url
        : imageDTO?.thumbnailUrl || imageDTO?.url || image;
  }

  if (!image) {
    image = productDefaultImage;
  }
  return image;
};

export const weightTypeList = [
  {
    label: 'Ounces',
    value: 'oz',
  },
  {
    label: 'Pounds',
    value: 'lbs',
  },
];

export const defaultListing = {
  size: 28,
  page: 0,
  category: [],
  tags: [],
  soldStartDate: undefined,
  soldEndDate: undefined,
  startDate: undefined,
  endDate: undefined,
  minVotes: undefined,
  minSold: undefined,
  price: [0, 1000],
  sortColumn: { path: '', order: '' },
  Catalog: [],
  recentlyAdded: true,
  mostUpVoted: false,
  mostSold: false,
  variationStockStatus: false,
  localDeliveryOnly : false,
  onlyMe : false,
  contributedFloristId : [],
  availabilityDateSet : false,
  deactivated : undefined,
};
